import React, { useState } from 'react';
import { Tabs, TabList, TabPanels, Tab, TabPanel, Box, HStack } from '@chakra-ui/core';
import isNil from 'lodash/isNil';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { BusinessTab } from './BusinessTab';
import { useStoreState } from '../../../models/hooks';
import { RecruiterWatchAndFollow } from './recruiterWatchAndFollow/RecruiterWatchAndFollow';
import { BulkAssignButton } from './BulkRecruiterWatchAndFollow/BulkAssignButton';

export type ChangeActiveHandler = {
  resetActiveTab: () => void;
};

const TABS = ['positions', 'monitor-by-position', 'monitor-by-recruiters'];

const PositionManagementComponent = (): JSX.Element => {
  const { t } = useTranslation();
  const location = useLocation();
  const [resetActiveBusiness, setResetActiveBusiness] = useState(false);
  const appUserId = useStoreState((s) => s.app.user?.id);
  const appOwnersId = useStoreState((s) => s.app.accounts?.owners);
  const adminPositionWatchAndFollowEnabled =
    useStoreState((s) => s.app.accounts?.configuration?.adminPositionWatchAndFollowEnabled) ?? false;
  const isAdminUser = !isNil(appOwnersId) && !isNil(appUserId) && appOwnersId?.includes(appUserId);
  const isWebAppyEnabled = useStoreState((s) => s.app.accounts?.configuration?.isBulkAssignModeEnabled);

  const getTabIndexFromSearch = React.useCallback((search: string) => {
    const queryParams = new URLSearchParams(search);
    const tab = queryParams.get('tab');
    const index = TABS.indexOf(tab || TABS[0]);
    return index === -1 ? /* istanbul ignore next */ 0 : index;
  }, []);

  const [tabIndex, setTabIndex] = useState(() => getTabIndexFromSearch(location.search));

  React.useEffect(() => {
    setTabIndex(getTabIndexFromSearch(location.search));
  }, [location.search, getTabIndexFromSearch]);

  const handleTabChange = (index: number) => {
    setTabIndex(index);
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('tab', TABS[index]);
    window.history.replaceState(null, '', `${location.pathname}?${queryParams.toString()}`);

    if (TABS[index] === 'positions') {
      setResetActiveBusiness(true);
    } else {
      setResetActiveBusiness(false);
    }
  };

  return (
    <Box sx={{ minHeight: 0, height: '100%' }}>
      <Tabs
        colorScheme="blue"
        index={tabIndex}
        onChange={handleTabChange}
        data-testid="ChangeTabIndexBtn"
        isLazy
        sx={{ minHeight: 0, height: '100%' }}
      >
        <HStack
          justify="space-between"
          width="100%"
          backgroundColor="white"
          marginBottom={tabIndex === 0 ? 6 : '2px'}
          borderRadius={5}
        >
          <TabList borderWidth={0} px={2} mb={0}>
            <Tab
              py={3}
              fontSize="sm"
              _selected={{ borderColor: 'blue.500', color: 'blue.500' }}
              _focus={{ boxShadow: 'none' }}
              minWidth="250px"
            >
              {t('createJobPosition:businessManagementTab.businessAndPosition')}
            </Tab>
            {isAdminUser && adminPositionWatchAndFollowEnabled && (
              <Tab
                py={3}
                fontSize="sm"
                _selected={{ borderColor: 'blue.500', color: 'blue.500' }}
                _focus={{ boxShadow: 'none' }}
                minWidth="250px"
              >
                {t('createJobPosition:businessManagementTab.watchAndFollowedPosition')}
              </Tab>
            )}
            {/* {isAdminUser && adminPositionWatchAndFollowEnabled && (
            <Tab
              py={3}
              fontSize="sm"
              _selected={{ borderColor: 'blue.500', color: 'blue.500' }}
              _focus={{ boxShadow: 'none' }}
              minWidth="250px"
            >
              Monitor By Recruiters
            </Tab>
          )} */}
          </TabList>
          {tabIndex === 1 && isWebAppyEnabled && <BulkAssignButton />}
        </HStack>
        <TabPanels sx={{ minHeight: 0, height: '100%' }}>
          <TabPanel p={0}>
            <BusinessTab resetActiveBusiness={resetActiveBusiness} />
          </TabPanel>
          {isAdminUser && adminPositionWatchAndFollowEnabled && (
            <TabPanel p={0} sx={{ minHeight: 0, height: '100%' }}>
              <RecruiterWatchAndFollow />
            </TabPanel>
          )}
          {/* {isAdminUser && adminPositionWatchAndFollowEnabled && (
            <TabPanel p={0} sx={{ minHeight: 0, height: '100%' }}>
            </TabPanel>
          )} */}
        </TabPanels>
      </Tabs>
    </Box>
  );
};

const PositionManagementWithContext = React.forwardRef<ChangeActiveHandler, unknown>(() => {
  return <PositionManagementComponent />;
});

PositionManagementWithContext.displayName = 'PositionManagementWithContext';
export const PositionManagementView = PositionManagementWithContext;
